import { useState } from 'react'
import { SeatStrip } from '@/shared/components/seat-strip'
import { UserPlus, CalendarPlus, Ban, Shield, ShieldCheck, Check, Flag } from 'lucide-react'
import { BottomSheet } from '@/shared/ui/bottom-sheet'
import { ReportPlayerSheet } from './report-player-sheet'
import { Button } from '@/shared/ui/button'
import { Avatar } from '@/shared/ui/avatar'
import { Chip } from '@/shared/ui/chip'
import { StatPill } from '@/shared/ui/stat-pill'
import { formatNivel } from '@/shared/levels'
import { formatLevelConfidence, formatVouching } from '@/shared/format'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { PLAYER_POSITION_LABELS } from '@/core/models/user'
import { usePublicProfile } from './use-public-profile'
import {
  useMyContacts,
  useSaveContact,
  useBlockPlayer,
  useRemoveContact,
} from '@/features/contactos/use-contacts-real'
import { getPlayerActions, invitePlayerToMatch, isInvitedTo, setPlayerActions } from '@/shared/player-actions'
import { showToast } from '@/shared/ui/toast'
import { useMyMatches, type MatchView } from '@/features/partidos/use-matches'
import { useSendInvites } from '@/features/partidos/use-match-invites'
import { findPlayer, freeSlots, myUpcomingMatches } from '@/shared/mock/data'

// Public player profile — the trust surface behind every player name in the
// app (roadmap #4): nivel, position, trust counters, quick actions. Real
// matches read `players_public/{uid}` (spec/06); the bypass keeps the
// certified mockPlayers path. Every action flips VISIBLE state (never a
// silent no-op — audit 2026-07-04 retired that class). Action state lives in
// the shared player-actions store so every surface agrees.
export function PlayerProfileSheet({
  name,
  uid = null,
  onClose,
}: {
  /** Player name to show, or null to keep the sheet closed. */
  name: string | null
  /** Real-account uid — switches the sheet to `players_public` facts. */
  uid?: string | null
  onClose: () => void
}) {
  const pub = usePublicProfile(AUTH_BYPASS ? null : uid)
  const real = !AUTH_BYPASS && !!uid
  const mockPlayer = name && !real ? findPlayer(name) : undefined
  // One display shape for both branches; `player` undefined = not available.
  const player = real
    ? pub.data
      ? {
          name: pub.data.name ?? (name || 'Jugador'),
          city: pub.data.city ?? '',
          level: pub.data.skillLevel,
          position: pub.data.position ? PLAYER_POSITION_LABELS[pub.data.position] : null,
          matches: pub.data.matchesPlayed,
          noShows: pub.data.noShows,
          guestShows: pub.data.guestShows,
          guestNoShows: pub.data.guestNoShows,
          levelSource: pub.data.levelSource,
          reliability: pub.data.levelReliability,
        }
      : undefined
    : mockPlayer
      ? {
          name: mockPlayer.name,
          city: mockPlayer.city,
          level: mockPlayer.level as number | null,
          position: mockPlayer.position as string | null,
          matches: mockPlayer.matches,
          noShows: mockPlayer.noShows,
          // The mock roster has no vouching history; inventing one would put a
          // fabricated trust claim on a design stub.
          guestShows: 0,
          guestNoShows: 0,
          levelSource: 'self' as string | null,
          reliability: null as number | null,
        }
      : undefined
  const [inviteSheet, setInviteSheet] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  // Real upcoming matches for the REAL branch; the mock list still drives the
  // bypass. `sentTo` is local because a fresh invite must show as sent without
  // waiting for a refetch — and it is only ever set AFTER the write lands.
  const myMatches = useMyMatches()
  const sendInvites = useSendInvites()
  const [sentTo, setSentTo] = useState<Set<string>>(new Set())
  const inviteOptions: MatchView[] = real
    ? (myMatches.data ?? []).filter((m) => m.status === 'active' || m.status === 'full')
    : []
  const [, forceRender] = useState(0)

  // Save/block state: real mode reads the contacts collection (R2); bypass
  // keeps the certified in-session store.
  const myContacts = useMyContacts()
  const saveMutation = useSaveContact()
  const blockMutation = useBlockPlayer()
  const unblockMutation = useRemoveContact()
  const realEntry = real ? (myContacts.data ?? []).find((e) => e.contact.contactUid === uid) : undefined
  const mockActions = name && !real ? getPlayerActions(name) : {}
  const saved = real ? realEntry?.contact.status === 'active' : (mockActions.saved ?? false)
  const blocked = real ? realEntry?.contact.status === 'blocked' : (mockActions.blocked ?? false)
  const noShowsDelta = !real ? (mockActions.noShowsDelta ?? 0) : 0
  const totalNoShows = (player?.noShows ?? 0) + noShowsDelta

  function setAction(patch: Parameters<typeof setPlayerActions>[1]) {
    if (!name) return
    setPlayerActions(name, patch)
    forceRender((n) => n + 1)
  }

  function onSave() {
    navigator.vibrate?.(10)
    if (real && uid) saveMutation.mutate(uid)
    else setAction({ saved: true })
  }

  function onBlock() {
    navigator.vibrate?.(15)
    if (real && uid) blockMutation.mutate(uid)
    else setAction({ blocked: true })
  }

  /**
   * Blocking was a ONE-WAY DOOR. "Bloquear" sits one mis-tap from "Ver perfil"
   * in the same sheet, the blocked player then vanishes from Contactos
   * entirely, and this sheet showed a static line with no way back — the
   * repository could unblock (`removeContact`) but no screen ever called it.
   * A safety control with no undo is its own trap.
   */
  function onUnblock() {
    navigator.vibrate?.(10)
    if (real && uid) unblockMutation.mutate(uid)
    else setAction({ blocked: false, saved: false })
  }

  // REAL invitations since 2026-08-10. This used to call
  // `invitePlayerToMatch(name, matchId)` — an in-memory store keyed by NAME —
  // for every player, real account or not. So under real auth the sheet listed
  // MOCK matches, and tapping one turned the row green with "Invitación
  // enviada" while nothing was written, nothing was sent, and nobody was
  // invited. A success confirmation for an action that did nothing, on a live
  // control. The real path already existed one folder away.
  async function onInvite(match: MatchView) {
    if (!name) return
    navigator.vibrate?.(10)
    if (!real || !uid) {
      // Bypass keeps the certified in-session store so design walks still work.
      invitePlayerToMatch(name, match.id)
      forceRender((n) => n + 1)
      return
    }
    try {
      await sendInvites.mutateAsync({ match, toUids: [uid] })
      setSentTo((prev) => new Set(prev).add(match.id))
      showToast('Invitación enviada')
    } catch {
      navigator.vibrate?.(20)
      showToast('No pudimos enviar la invitación. Inténtalo de nuevo.')
    }
  }

  return (
    <>
      <BottomSheet open={!!name && !inviteSheet} onClose={onClose} title="Perfil">
        {real && pub.isPending ? (
          <div className="flex flex-col gap-3 py-4" aria-busy>
            <div className="h-16 w-16 mx-auto rounded-full bg-[var(--panel-2)] motion-safe:animate-pulse" />
            <div className="h-4 w-32 mx-auto rounded bg-[var(--panel-2)] motion-safe:animate-pulse" />
          </div>
        ) : player ? (
          <div className="flex flex-col gap-4 pb-2">
            <div className="flex flex-col items-center text-center gap-2">
              <Avatar name={player.name} size={72} />
              <div>
                <p className="font-bold text-[length:var(--fs-lg)]">{player.name}</p>
                <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">{player.city}</p>
              </div>
              <div className="flex gap-2">
                {player.level != null && <Chip active>Nivel {formatNivel(player.level)}</Chip>}
                {player.position && <Chip>{player.position}</Chip>}
              </div>
              {/* Qualify the level on EVERY source, not just calibrated ones.
                  A self-declared 3.5 used to render as a bare number, visually
                  identical to one confirmed across a dozen competitive matches
                  — which is exactly what a casual player needs to tell apart
                  before joining strangers. The raw "fiabilidad 85%" is gone on
                  purpose: it is the ELO K-factor input (starts 10, +15/match,
                  caps at 100 in ~6 matches), so a percentage invited precision
                  the number does not carry. */}
              {formatLevelConfidence(player.levelSource, player.reliability) && (
                <p className="flex items-center gap-1.5 text-[length:var(--fs-xs)] text-[color:var(--fg-faint)]">
                  {player.levelSource === 'computed' ? (
                    <ShieldCheck size={13} className="text-[color:var(--ok)]" />
                  ) : (
                    <Shield size={13} />
                  )}
                  {formatLevelConfidence(player.levelSource, player.reliability)}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <StatPill value={player.matches} label="Partidos" />
              <StatPill value={totalNoShows} label="Ausencias" />
            </div>

            {/* The VOUCHING line, and it earns its place: "2 ausencias" used to
                mean either "this player flakes" or "this player brings people
                who flake", and those are opposite answers to the question the
                reader is actually asking. Rendered only for players who have
                brought somebody — a zero on every profile would be noise. */}
            {formatVouching(player.guestShows, player.guestNoShows) && (
              <p className="flex items-center justify-center gap-2 text-center text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                <UserPlus size={15} className={player.guestNoShows === 0 ? 'text-[color:var(--ok)]' : 'text-[color:var(--fg-faint)]'} />
                {formatVouching(player.guestShows, player.guestNoShows)}
              </p>
            )}

            {!real && mockPlayer && (
              <p className="flex items-center justify-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                <CalendarPlus size={15} className="text-[color:var(--accent)]" />
                Acepta {mockPlayer.invitesAccepted} de {mockPlayer.invitesReceived} invitaciones.
              </p>
            )}

            {totalNoShows === 0 && player.matches > 0 && (
              <p className="flex items-center justify-center gap-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                <Shield size={15} className="text-[color:var(--ok)]" />
                Siempre se ha presentado a sus partidos.
              </p>
            )}

            {blocked ? (
              /* Gap B2: previously read "no verá tus partidos". Blocking is a
                 client-side feed filter — the blocked player can still read and
                 join. Describe the real behaviour until server-enforced blocks
                 ship (deferred to the public-beta hardening pass). */
              <div className="um-enter flex flex-col items-center gap-2">
                <p className="flex items-center justify-center gap-2 text-[length:var(--fs-sm)] font-semibold text-[color:var(--bad)]">
                  <Ban size={15} /> Bloqueado — no verás sus partidos.
                </p>
                <Button variant="outline" size="pill" onClick={onUnblock}>
                  Desbloquear
                </Button>
              </div>
            ) : (
              <div className="flex flex-col">
                <SheetAction
                  icon={saved ? <Check size={20} /> : <UserPlus size={20} />}
                  done={saved}
                  onClick={saved ? undefined : onSave}
                >
                  {saved ? 'Guardado en contactos' : 'Guardar contacto'}
                </SheetAction>
                <SheetAction icon={<CalendarPlus size={20} />} onClick={() => setInviteSheet(true)}>
                  Invitar a un partido
                </SheetAction>
                <SheetAction icon={<Ban size={20} />} danger onClick={onBlock}>
                  Bloquear
                </SheetAction>
                {/* Reporting is NOT blocking. Blocking is a private, instant,
                    one-way door the player controls; a report asks a person to
                    look. Offering only "Bloquear" made the app's answer to bad
                    behaviour "hide it yourself" — which leaves the behaviour
                    aimed at everybody else. Real accounts only: there is nobody
                    to report on a design fixture. */}
                {real && uid && (
                  <SheetAction icon={<Flag size={20} />} onClick={() => setReportOpen(true)}>
                    Denunciar
                  </SheetAction>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="py-4 text-center text-[color:var(--fg-muted)]">
            {real
              ? 'Sin datos todavía — este jugador aún no tiene historial en uPadelMatch.'
              : 'Perfil no disponible.'}
          </p>
        )}
      </BottomSheet>

      <BottomSheet
        open={inviteSheet}
        onClose={() => setInviteSheet(false)}
        title={player ? `Invitar a ${player.name.split(' ')[0]}` : undefined}
      >
        <div className="flex flex-col gap-2 pb-2">
          {real && myMatches.isError ? (
            /* A failed read is not "you have no matches" — that would send a
               player to create one they already have. */
            <p className="py-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              No pudimos cargar tus partidos.{' '}
              <button
                type="button"
                onClick={() => void myMatches.refetch()}
                className="font-semibold text-[color:var(--accent)]"
              >
                Reintentar
              </button>
            </p>
          ) : (real ? inviteOptions : myUpcomingMatches().filter((m) => !name || !m.players.some((pl) => pl.name === name))).length === 0 ? (
            <p className="py-2 text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
              No tienes partidos próximos donde invitar — crea uno primero.
            </p>
          ) : (
            <>
              <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">Tus próximos partidos:</p>
              {(real
                ? inviteOptions
                : myUpcomingMatches().filter((m) => !name || !m.players.some((pl) => pl.name === name))
              )
                .map((m) => {
                  const sent = real
                    ? sentTo.has(m.id)
                    : name
                      ? isInvitedTo(name, m.id)
                      : false
                  const free = freeSlots(m)
                  return (
                    <button
                      key={m.id}
                      type="button"
                      disabled={sent || free === 0}
                      onClick={() => void onInvite(m)}
                      className="flex items-center gap-3 min-h-[var(--tap)] px-1 rounded-[var(--radius)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform text-left"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{m.place}</p>
                        <p className="text-[length:var(--fs-sm)] text-[color:var(--fg-muted)]">
                          {m.dayLabel} · {m.timeLabel}
                        </p>
                        {/* Boxes, not a count — same primitive as the feed, the
                            door and the detail, so "how full is it" reads the
                            same everywhere. */}
                        <SeatStrip
                          players={m.players}
                          companions={m.offAppCompanions ?? 0}
                          capacity={m.capacity}
                          size={20}
                          className="mt-1"
                        />
                      </div>
                      {sent ? (
                        <span className="um-enter shrink-0 text-[length:var(--fs-sm)] font-semibold text-[color:var(--ok)]">
                          Invitación enviada
                        </span>
                      ) : (
                        <CalendarPlus size={18} className="shrink-0 text-[color:var(--accent)]" />
                      )}
                    </button>
                  )
                })}
            </>
          )}
        </div>
      </BottomSheet>
      <ReportPlayerSheet
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        reportedUid={uid}
        reportedName={player?.name ?? null}
      />
    </>
  )
}

function SheetAction({
  icon,
  children,
  onClick,
  danger,
  done,
}: {
  icon: React.ReactNode
  children: React.ReactNode
  onClick?: (() => void) | undefined
  danger?: boolean
  done?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`flex items-center gap-3 min-h-[var(--tap)] px-1 rounded-[var(--radius)] hover:bg-[var(--line-soft)] active:scale-[0.99] transition-transform ${
        danger ? 'text-[color:var(--bad)]' : done ? 'text-[color:var(--ok)]' : ''
      }`}
    >
      <span className={danger || done ? '' : 'text-[color:var(--fg-muted)]'}>{icon}</span>
      <span className="font-medium">{children}</span>
    </button>
  )
}
